import { fail, mergeResults, ok, type ValidationResult } from "./validationHelpers";

type OptionLike = {
  code?: string;
  isCorrect?: boolean;
};

export function validateQuestionOptions(
  options: OptionLike[] | undefined
): ValidationResult {
  if (!Array.isArray(options) || options.length < 2) {
    return fail("options must contain at least 2 items");
  }

  const codes = new Set<string>();
  let correctCount = 0;

  for (let i = 0; i < options.length; i++) {
    const option = options[i];
    if (!option || typeof option !== "object") {
      return fail(`options[${i}] must be an object`);
    }

    const code = option.code;
    if (typeof code !== "string" || code.trim().length === 0) {
      return fail(`options[${i}].code is required`);
    }
    if (codes.has(code)) {
      return fail(`duplicate option code: ${code}`);
    }
    codes.add(code);

    if (typeof option.isCorrect !== "boolean") {
      return fail(`options[${i}].isCorrect must be a boolean`);
    }
    if (option.isCorrect) {
      correctCount++;
    }
  }

  if (correctCount < 1) {
    return fail("at least one option must have isCorrect: true");
  }

  return ok;
}

export function validateQuestionOptionImport(
  value: unknown,
  path: string
): ValidationResult {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return fail(`${path} must be an object`);
  }
  const option = value as Record<string, unknown>;

  const results: ValidationResult[] = [
    requireLocalizedStringInline(option.text, `${path}.text`),
  ];

  if (option.code !== undefined) {
    results.push(requireNonEmptyStringInline(option.code, `${path}.code`));
  }

  if (typeof option.isCorrect !== "boolean") {
    results.push(fail(`${path}.isCorrect is required (boolean)`));
  }

  return mergeResults(...results);
}

function requireNonEmptyStringInline(
  value: unknown,
  field: string
): ValidationResult {
  if (typeof value !== "string" || value.trim().length === 0) {
    return fail(`${field} is required`);
  }
  return ok;
}

function requireLocalizedStringInline(
  value: unknown,
  field: string
): ValidationResult {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return fail(`${field} must have at least one non-empty translation`);
  }
  const hasValue = Object.values(value as Record<string, unknown>).some(
    (v) => typeof v === "string" && v.trim().length > 0
  );
  if (!hasValue) {
    return fail(`${field} must have at least one non-empty translation`);
  }
  return ok;
}

/** Import authoring: `code` optional (filled by normalize); `isCorrect` required. */
export function validateQuestionOptionsImport(
  options: unknown
): ValidationResult {
  if (!Array.isArray(options) || options.length < 2) {
    return fail("options must be a non-empty array with at least 2 items");
  }

  const codes = new Set<string>();
  for (let i = 0; i < options.length; i++) {
    const raw = options[i];
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
      continue;
    }
    const code = (raw as Record<string, unknown>).code;
    if (typeof code === "string") {
      if (code.trim().length === 0) {
        return fail(`options[${i}].code must be non-empty when provided`);
      }
      if (codes.has(code)) {
        return fail(`duplicate option code: ${code}`);
      }
      codes.add(code);
    }
  }

  const results: ValidationResult[] = [];
  for (let i = 0; i < options.length; i++) {
    results.push(validateQuestionOptionImport(options[i], `options[${i}]`));
  }
  const merged = mergeResults(...results);
  if (!merged.ok) {
    return merged;
  }

  const withExplicitCorrect = options.some(
    (o) =>
      o &&
      typeof o === "object" &&
      !Array.isArray(o) &&
      (o as Record<string, unknown>).isCorrect === true
  );

  if (!withExplicitCorrect) {
    return fail("at least one option must have isCorrect: true");
  }

  return ok;
}
